import React, { useState, useEffect, useRef } from 'react';
import { VitalsColorKeys, vitalColors } from '../../theme/colors';
import styled from 'styled-components';

// Define the prop types for the component
interface TimedValueComponentProps {
  value: number;
  valid: boolean;
  small?: boolean;
  unit: string;
  displayColor?: VitalsColorKeys;
  precision?: number;
}

interface StyledSpanProps {
  displaycolor: VitalsColorKeys | string;
  small: boolean;
}

interface StyledSmallProps {
  displaycolor: VitalsColorKeys | string;
}

const SmallText = styled.small<StyledSmallProps>`
  color: ${props => props.displaycolor};
`;

const Text = styled.span<StyledSpanProps>`
  font-size: ${props => (props.small ? '1.5' : '2')}em;
  color: ${props => props.displaycolor};
  @media (min-width: 576px) {
    font-size: ${props => (props.small ? '1.5' : '2.5')}em;
  }
  @media (min-width: 768px) {
    font-size: ${props => (props.small ? '2.25' : '3')}em;
  }
  @media (min-width: 992px) {
    font-size: ${props => (props.small ? '2.75' : '3.5')}em;
  }
  @media (min-width: 1200px) {
    font-size: ${props => (props.small ? '3' : '4')}em;
  }
  @media (min-width: 1400px) {
    font-size: ${props => (props.small ? '3.75' : '4.5')}em;
  }
`;

// Debounce hook
function useDebouncedEffect(callback: () => void, deps: unknown[], delay: number) {
  const handler = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (handler.current) clearTimeout(handler.current);
    handler.current = setTimeout(() => {
      callback();
    }, delay);

    return () => {
      if (handler.current) clearTimeout(handler.current);
    };
  }, [...deps, delay]);
}

const TimedValueComponent: React.FC<TimedValueComponentProps> = ({
  value,
  valid,
  displayColor = 'white',
  small = false,
  unit,
  precision
}) => {
  const [displayValue, setDisplayValue] = useState<string | number>('--');
  const [color, setColor] = useState<VitalsColorKeys>('grey');
  const [version, setVersion] = useState(0);
  const greyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resetValueTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const toFixed = precision ?? 2;

  const clearTimers = () => {
    if (greyTimeoutRef.current) clearTimeout(greyTimeoutRef.current);
    if (resetValueTimeoutRef.current) clearTimeout(resetValueTimeoutRef.current);
  };

  // Debounce value changes to avoid flicker / jitter from rapid updates
  useDebouncedEffect(() => {
    if (valid && value !== -1 && value != null) {
      setDisplayValue(value);
      setColor(displayColor);
      setVersion(v => v + 1);
    }
  }, [value, valid, displayColor], 1000); // 1 second debounce

  // Reset timers when version changes
  useEffect(() => {
    clearTimers();

    greyTimeoutRef.current = setTimeout(() => {
      setColor('grey');
    }, 5 * 60 * 1000);

    resetValueTimeoutRef.current = setTimeout(() => {
      setDisplayValue('--');
    }, 10 * 60 * 1000);

    return clearTimers;
  }, [version]);

  useEffect(() => clearTimers, []);

  return (
    <>
      <Text displaycolor={vitalColors[color]} small={small}>
        {typeof displayValue === 'number' ? displayValue.toFixed(toFixed) : displayValue}
      </Text>
      <SmallText displaycolor={vitalColors[color]}>{unit}</SmallText>
    </>
  );
};

export default TimedValueComponent;